/**
 * Invitation Models
 *
 * Data models for the Organization Invitation system.
 * Supports inviting existing Firebase users and new users to organizations
 * with optional position and hat assignments.
 */

import { BaseDocumentFields, UID } from '../general-models';
import { NewgenusTimestamp } from '../shared-models/timestamp';

// ============================================================
// Enums
// ============================================================

/**
 * Status of an organization invitation.
 * Defined as const object + union type for cross-module compatibility
 * (allows both import type and regular import to work correctly).
 */
export const InvitationStatus = {
    /** Invitation sent but not yet acted upon */
    PENDING: 'pending',
    /** Invitation accepted by the recipient */
    ACCEPTED: 'accepted',
    /** Invitation declined by the recipient */
    DECLINED: 'declined',
    /** Invitation expired (past expiresOn date) */
    EXPIRED: 'expired',
    /** Invitation cancelled by the inviter or admin */
    CANCELLED: 'cancelled',
} as const;
export type InvitationStatus = typeof InvitationStatus[keyof typeof InvitationStatus];

/**
 * Type of invitation based on whether the user exists in Firebase Auth.
 * Defined as const object + union type for cross-module compatibility.
 */
export const InvitationType = {
    /** User already has a Firebase Auth account */
    EXISTING_USER: 'existing_user',
    /** User does not have a Firebase Auth account */
    NEW_USER: 'new_user',
} as const;
export type InvitationType = typeof InvitationType[keyof typeof InvitationType];

// ============================================================
// Core Invitation Document
// ============================================================

/**
 * Represents an invitation stored in Firestore `invitations/` collection.
 * This document is created when a user is invited to join an organization.
 */
export interface InvitationDocument extends BaseDocumentFields {
    /** Unique invitation token used in the acceptance link */
    token: string;

    /** Email address of the invited user */
    email: string;

    /** Type of invitation (existing vs new user) */
    type: InvitationType;

    /** Current status of the invitation */
    status: InvitationStatus;

    // ----- Organization Context -----

    /** Key of the organization the user is invited to */
    orgKey: string;

    /** Name of the organization (denormalized for email/display) */
    orgName: string;

    // ----- Inviter Info -----

    /** UID of the user who sent the invitation */
    invitedBy: UID;

    /** Display name of the inviter (denormalized for email/display) */
    inviterName: string;

    /** Email of the inviter */
    inviterEmail: string;

    // ----- Optional Assignments -----

    /**
     * Positions (OrgPart keys) to assign the user to upon acceptance.
     * Each position's partKey from the org tree.
     */
    positionKeys?: string[];

    /**
     * Position details denormalized for display in the acceptance page.
     */
    positions?: InvitationPositionInfo[];

    /**
     * Hats (OrgHat keys) to assign the user to upon acceptance.
     */
    hatKeys?: string[];

    /**
     * Hat details denormalized for display in the acceptance page.
     */
    hats?: InvitationHatInfo[];

    // ----- Timing -----

    /** When the invitation was sent */
    sentOn: NewgenusTimestamp;

    /** When the invitation expires */
    expiresOn: NewgenusTimestamp;

    /** When the invitation was accepted/declined */
    respondedOn?: NewgenusTimestamp;

    // ----- Recipient Info (populated after acceptance) -----

    /** UID of the user who accepted (may differ from original if forwarded) */
    acceptedByUid?: UID;

    /**
     * UID of the resolved existing-user the invitation was created for.
     *
     * Set ONLY when the invitation is created from the Create-Organization wizard's
     * staff stage for a row whose `resolutionStatus === 'existing-user'` (i.e. the
     * email was matched to a known Firebase Auth uid before the invite was sent).
     *
     * Why this exists (Phase B):
     * - The Approve-pending-user gate (org-board) needs to ask "does this uid have a
     *   pending invitation in this org?" cheaply. Without `targetUid` we'd need a
     *   reverse email → invitations lookup on every check.
     * - The notification-bell in-app accept surface (B.4) filters invitations by the
     *   logged-in user's uid; `targetUid` makes that a single indexed query.
     * - `rollbackWizardPrestageOnInvitationDeath()` uses it to know which uid to
     *   remove from `pendingAssignedToArray` without re-resolving by email.
     *
     * NOT set for: classic invite-by-email-only invitations, or for `new_user` type
     * invitations (no uid exists yet — gets populated as `acceptedByUid` post-accept).
     */
    targetUid?: UID;

    /** Optional personal message from the inviter */
    message?: string;

    // ----- Email Tracking -----

    /** Whether the invitation email was successfully sent */
    emailSent?: boolean;

    /** ID of the email queue item for tracking delivery */
    emailQueueKey?: string;

    /** Whether a reminder email was sent */
    reminderSent?: boolean;

    /** When the reminder was sent */
    reminderSentOn?: NewgenusTimestamp;
}

// ============================================================
// Supporting Types
// ============================================================

/**
 * Lightweight position info stored on the invitation for display.
 */
export interface InvitationPositionInfo {
    partKey: string;
    name: string;
    path: string;
    depth: number;
}

/**
 * Lightweight hat info stored on the invitation for display.
 */
export interface InvitationHatInfo {
    key: string;
    name: string;
    description?: string;
    securityGroupNames?: string[];
}

// ============================================================
// API Request / Response Types
// ============================================================

/**
 * Request payload for creating invitation(s) - sent from frontend to backend.
 */
export interface CreateInvitationsRequest {
    /** Organization ID to invite users to */
    orgKey: string;

    /** List of invitation details */
    invitations: InvitationInput[];
}

/**
 * Single invitation input from the invite dialog.
 */
export interface InvitationInput {
    /** Email address to invite */
    email: string;

    /** Optional position keys to pre-assign */
    positionKeys?: string[];

    /** Optional hat keys to pre-assign */
    hatKeys?: string[];

    /** Optional personal message */
    message?: string;
}

/**
 * Response from the create invitations endpoint.
 */
export interface CreateInvitationsResponse {
    /** Total number of invitations processed */
    total: number;

    /** Successfully created invitations */
    created: InvitationResult[];

    /** Failed invitations with reasons */
    failed: InvitationFailure[];
}

/**
 * Result for a successfully created invitation.
 */
export interface InvitationResult {
    email: string;
    invitationKey: string;
    type: InvitationType;
    status: InvitationStatus;
}

/**
 * Result for a failed invitation.
 */
export interface InvitationFailure {
    email: string;
    reason: string;
}

/**
 * Request payload for accepting an invitation.
 */
export interface AcceptInvitationRequest {
    /** The invitation token from the email link */
    token: string;
}

/**
 * Response from the accept invitation endpoint.
 */
export interface AcceptInvitationResponse {
    success: boolean;
    orgKey: string;
    orgName: string;
    /** Message to display to the user */
    message: string;
    /** Redirect URL after acceptance */
    redirectUrl?: string;
}

/**
 * Public invitation details returned for the acceptance page.
 * This is a safe subset of InvitationDocument (no sensitive data).
 */
export interface InvitationPublicInfo {
    token: string;
    email: string;
    type: InvitationType;
    status: InvitationStatus;
    orgName: string;
    inviterName: string;
    positions?: InvitationPositionInfo[];
    hats?: InvitationHatInfo[];
    expiresOn: NewgenusTimestamp;
    message?: string;
    /** Whether the invitation has expired */
    isExpired: boolean;
    /** Whether the invitation is still pending */
    isPending: boolean;
}

/**
 * Invitation dialog result returned from the enhanced invite user dialog.
 * This is the data structure passed from the dialog to the org-board-service.
 */
export interface InviteUserDialogResult {
    /** List of invitation inputs */
    invitations: InvitationInput[];
}

// ================================================================
// Email Status Check Types
// ================================================================

/**
 * Request body for the email status check endpoint.
 */
export interface CheckEmailStatusRequest {
    /** Email address to check */
    email: string;
    /** Organization key to check membership against */
    orgKey: string;
}

/**
 * Response from the email status check endpoint.
 */
export interface CheckEmailStatusResponse {
    /** Whether the email exists as a Firebase Auth user */
    existsInAuth: boolean;
    /** Whether the email is already a member of the specified organization */
    existsInOrg: boolean;
    /** Display name from Firebase Auth (if user exists) */
    displayName?: string;
    /**
     * Firebase Auth uid for the resolved user (present iff `existsInAuth` is
     * true). Consumed by the org-creation wizard's staff stage so existing-user
     * rows can be pre-staged into `pendingApprovals` without a second lookup.
     */
    uid?: string;
    /** Whether there is already a pending invitation for this email */
    hasPendingInvitation?: boolean;
}

// ================================================================
// Invitation Management Types (List / Resend / Revoke)
// ================================================================

/**
 * Request body for listing organization invitations.
 */
export interface ListInvitationsRequest {
    /** Organization key */
    orgKey: string;
    /** Optional status filter */
    status?: InvitationStatus;
}

/**
 * Response from the list invitations endpoint.
 */
export interface ListInvitationsResponse {
    /** Array of invitation documents */
    invitations: InvitationDocument[];
    /** Total count */
    total: number;
}

/**
 * Request body for resending an invitation.
 */
export interface ResendInvitationRequest {
    /** Key of the invitation to resend */
    invitationKey: string;
}

/**
 * Response from the resend invitation endpoint.
 */
export interface ResendInvitationResponse {
    success: boolean;
    message: string;
    /** Updated invitation key (same key, new token) */
    invitationKey?: string;
}

/**
 * Request body for revoking (cancelling) an invitation.
 */
export interface RevokeInvitationRequest {
    /** Key of the invitation to revoke */
    invitationKey: string;
}

/**
 * Response from the revoke invitation endpoint.
 */
export interface RevokeInvitationResponse {
    success: boolean;
    message: string;
}

/**
 * Request body for deleting an invitation record.
 */
export interface DeleteInvitationRequest {
    /** Key of the invitation to delete */
    invitationKey: string;
}

/**
 * Response from the delete invitation endpoint.
 */
export interface DeleteInvitationResponse {
    success: boolean;
    message: string;
}
